import { LocalStorage } from './LocalStorage';

type Settings<
  TLocalStorageModel extends Record<string, any>,
  TFrom = TLocalStorageModel,
  TTo = TLocalStorageModel
> = {
  guard: (x: unknown) => x is TLocalStorageModel;
  toLocalStorageModel: (from: TFrom) => TLocalStorageModel;
  fromLocalStorageModel: (from: TLocalStorageModel) => TTo;
};

export class LocalStorageContainer<
  TLocalStorageModel extends Record<string, any>,
  TFrom = TLocalStorageModel,
  TTo = TLocalStorageModel
> {
  #guard: (x: unknown) => x is TLocalStorageModel;
  #toLocalStorageModel: (from: TFrom) => TLocalStorageModel;
  #fromLocalStorageModel: (from: TLocalStorageModel) => TTo;

  constructor(
    private readonly key: string,
    settings: Partial<Settings<TLocalStorageModel, TFrom, TTo>> = {}
  ) {
    this.#guard = settings.guard ?? ((_): _ is TLocalStorageModel => true);
    // @ts-expect-error
    this.#toLocalStorageModel = settings.toLocalStorageModel ?? (x => x);
    // @ts-expect-error
    this.#fromLocalStorageModel = settings.fromLocalStorageModel ?? (x => x);
  }

  get = () => {
    const raw = LocalStorage.get(this.key);

    if (raw) {
      try {
        const json = JSON.parse(raw);

        if (this.#guard(json)) {
          return this.#fromLocalStorageModel(json);
        }
      } catch (e) {}
    }

    return null;
  };

  set = (value: TFrom) => {
    LocalStorage.set(this.key, JSON.stringify(this.#toLocalStorageModel(value)));
  };

  delete = () => {
    LocalStorage.remove(this.key);
  };
}
